"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar as CalendarIcon, Loader, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border text-center">
        <p className="font-default text-xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[--gold-accent] text-[--gold-accent] hover:bg-[--gold-accent] hover:text-white">
          Solicitar otra consulta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "El nombre es obligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
              Nombre Completo
            </FormLabel>
            <FormControl>
              <Input placeholder="María García" className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base h-11" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "El email es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
                Correo Electrónico
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="maria@ejemplo.com" className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base h-11" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
                Teléfono
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+34 600 000 000" className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base h-11" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="workshop_date" rules={{
      required: "Por favor, seleccione una fecha"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
              Fecha Preferida
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="date" className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base h-11 pr-8" {...field} />
                <SafeHydrate><CalendarIcon className="absolute right-0 top-1/2 -translate-y-1/2 size-4 text-[--gold-accent] pointer-events-none" /></SafeHydrate>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="session_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
              Tipo de Consulta
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus:ring-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base h-11 w-full">
                  <SelectValue placeholder="Seleccione su interés" />
                </SelectTrigger>
                <SelectContent className="font-default">
                  <SelectItem value="medicina">Medicina Capilar</SelectItem>
                  <SelectItem value="cirugia">Cirugía Capilar</SelectItem>
                  <SelectItem value="seguimiento">Seguimiento de Tratamiento</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-[0.15em] font-default text-[--gold-accent] font-bold">
              Notas Adicionales
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Cuéntenos brevemente su caso..." className="border-0 border-b border-[--blush-soft] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[--gold-accent] font-default text-base resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subscribe" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-1 border-[--blush-soft]" />
            </FormControl>
            <FormLabel className="font-default text-sm text-foreground leading-relaxed font-normal cursor-pointer">
              Acepto recibir información sobre salud capilar.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 bg-[--destructive]/10 border border-[--destructive]/30">
          <p className="text-[--destructive] font-default text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-[--gold-accent] text-white hover:bg-[--navy-deep] font-default text-base tracking-wide">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Enviando...
          </> : <>Solicitar Cita →</>}
      </Button>
    </Form>;
}
export default function ConsultaDeValoraciNMDica() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return;
}